/**
 * The operator surface's JSON shapes.
 *
 * Hand-written, and that is a decision rather than laziness: `/v1/admin/*` and `/v1/fund/*` are
 * deliberately NOT in `docs/api/openapi.yaml` (apps/api/README.md — that contract is the
 * customer API), so there is nothing to generate from. Every type below mirrors one serializer
 * in `apps/api/src/lib/serialize.ts` or one inline shape in `apps/api/src/routes/admin.ts`, and
 * the field names are copied from there, not invented here.
 *
 * SCALES ARE IN THE NAMES, because the API publishes integers and never floats (BUILD.md §3):
 * `_bp` is basis points of one SUN (29,000 = 2.9 SUN), `_centi_sun` is hundredths of a SUN
 * (2,000 = 20.00 SUN), `_sun` is whole SUN, `_micro` is a ratio × 1e6.
 */

/**
 * What a SCREEN may do: read, or also change. A screen with a mutation names it in its props. The session's own role
 * (viewer / editor / admin / root, docs/product/admin-auth.md §1) maps onto it: everything from editor up edits.
 */
export type AdminRole = "viewer" | "editor";

/** The admin's role as the API holds it. */
export type AdminSessionRole = "viewer" | "editor" | "admin" | "root";

export interface AdminIdentity {
  address: string;
  role: AdminSessionRole;
  method: "tron" | "ethereum" | "passkey";
}

const RANK: Record<AdminSessionRole, number> = { viewer: 0, editor: 1, admin: 2, root: 3 };

export function roleAtLeast(role: AdminSessionRole, wanted: AdminSessionRole): boolean {
  return RANK[role] >= RANK[wanted];
}

export function screenRoleOf(role: AdminSessionRole): AdminRole {
  return roleAtLeast(role, "editor") ? "editor" : "viewer";
}

export interface VolumeGridEntry {
  min_amount: number;
  price_sun_per_unit: number;
}

/** A row of the retail grid in force now (`GET /v1/admin/prices`). */
export interface ActivePriceRow {
  id: string;
  brand_id: string | null;
  resource: string;
  tier: string;
  day_part: string | null;
  base_price_sun_per_unit: number;
  day_part_factor_bp: number;
  brand_markup_bp: number;
  min_amount: number | null;
  max_amount: number | null;
  activation_price_sun: number | null;
  visible: boolean;
  sales_paused: boolean;
  effective_at: string;
  effective_until: string | null;
}

/** One published version of the retail grid (`GET /v1/admin/prices/versions`). */
export interface PriceVersion {
  id: string;
  brand_id: string | null;
  resource: string;
  tier: string;
  day_part: string | null;
  base_price_sun_per_unit: number;
  day_part_factor_bp: number;
  brand_markup_bp: number;
  volume_grid: VolumeGridEntry[] | null;
  min_amount: number | null;
  max_amount: number | null;
  activation_price_sun: number | null;
  visible: boolean;
  sales_paused: boolean;
  effective_from: string;
  effective_until: string | null;
  changed_by: string | null;
  reason: string | null;
  created_at: string;
}

export interface FloorVersion {
  id: string;
  resource: string;
  tier: string;
  day_part: string | null;
  price_centi_sun_per_unit: number;
  mode: string;
  effective_from: string;
  changed_by: string | null;
  reason: string | null;
  created_at: string;
}

/** One routing-fee version: basis points of the provider price (500 = 5 %). */
export interface RouterFeeVersion {
  id: string;
  fee_bps: number;
  effective_from: string;
  changed_by: string | null;
  reason: string | null;
  created_at: string;
}

/** `GET /admin/router-fee`. With no version ever published the API reports 0 bps and a null start. */
export interface RouterFeeView {
  active: { fee_bps: number; effective_from: string | null };
  versions: RouterFeeVersion[];
}

/** One row of a routing-fee schedule: from this 30-day spend (SUN) upwards, this fee (bps). */
export interface RouterFeeTier {
  from_sun: number;
  fee_bps: number;
}

/** One routing-fee schedule version: the whole tier table, never a single row. */
export interface RouterFeeSchedule {
  id: string;
  tiers: RouterFeeTier[];
  effective_from: string;
  changed_by: string | null;
  reason: string | null;
  created_at: string;
}

/** `GET /admin/router-fee/schedules`. With no version ever published `tiers` is empty and the start null. */
export interface RouterFeeSchedulesView {
  active: { tiers: RouterFeeTier[]; effective_from: string | null };
  versions: RouterFeeSchedule[];
}

/** `GET /admin/promo-token` (viewer): the promo token redeem (docs/product/promo-redeem.md). */
export interface PromoTokenView {
  /** Null: no promo on this network or no token configured. */
  token_id: string | null;
  forward_address: string | null;
  settings: PromoTokenSettings;
  state: "active" | "cap_reached_today" | "not_started" | "ended" | "off";
  redeemed_today: number;
  items: Array<{
    address: string;
    method: "signature" | "forward";
    redeemed_at: string;
    status: "granting" | "delegated" | "failed";
    energy: number;
    expires_at: string | null;
    order_id: string | null;
    forward_txid: string | null;
  }>;
}

export interface PromoTokenSettings {
  cap_per_day: number;
  starts_at: string | null;
  ends_at: string | null;
  updated_at: string;
  updated_by: string | null;
}

/** `PATCH /admin/promo-token` (editor). */
export interface PromoTokenPatch {
  cap_per_day?: number;
  starts_at?: string | null;
  ends_at?: string | null;
}

/** `GET /admin/promo-token/drops` (viewer): the automatic drops (docs/product/promo-redeem.md §Automatic drops). */
/** Why an address got the token (migration 0107); null on drops made before it was kept. */
export interface DropContext {
  why: "new_account" | "manual" | "script" | "payer" | "client" | "activator";
  venues?: string[];
  decile?: number;
  tier0?: boolean;
  of?: string;
}

export interface PromoDropsView extends PromoDropState {
  network: "nile" | "mainnet";
  token_id: string | null;
  drops_24h: number;
  skips_24h: number;
  /** Every drop of this network's token ever. */
  drops_total: number;
  drops: Array<{ address: string; sender: string; txid: string; sent_at: string; context?: DropContext | null }>;
  skips: Array<{ address: string; reason: string; block_number: number | null; at: string }>;
}

/** `POST /admin/promo-token/drops/send` (editor): queued for the worker's Dropper; the result lands in the drops log. */
export interface PromoSendQueued {
  queued: true;
  address: string;
  tokens: number;
  token_id: string;
}

/**
 * `GET /admin/promo-token/funnel?from&to` (tenergy.app) and `POST /admin/promo-token/funnel/dev` (tenergy.dev), both
 * mainnet, viewer. The cohort is the wallets sent to in [from, to). `prior`: customers who had topped up and ordered
 * before the claim or entry; counted in `claimed`, never in `paying`.
 */
export interface PromoFunnel {
  token: "app" | "dev";
  token_id: string | null;
  from: string;
  to: string;
  sent: number;
  claimed: number;
  paying: number;
  prior: number;
}

/** `GET /admin/promo-token/drops/cohort?from&to` (viewer): the addresses this network's token was sent to. */
export interface PromoCohort {
  network: "nile" | "mainnet";
  token_id: string | null;
  from: string;
  to: string;
  addresses: string[];
}

/** A funnel period, ISO instants, `to` exclusive. */
export interface FunnelPeriod {
  from: string;
  to: string;
}

/** `PATCH /admin/promo-token/drops` (editor) answer. */
export interface PromoDropState {
  enabled: boolean;
  /** Index of the sender the next drop goes from. */
  next_sender: number;
  /** Set while drops are stopped because the next sender cannot pay one. */
  stopped_at: string | null;
  updated_at: string;
  updated_by: string | null;
}

/** `GET /admin/promo-codes` row. `ends_at`/`max_uses` null = no end / unlimited. */
export interface PromoCode {
  id: string;
  code: string;
  fee_bps: number;
  duration_days: number;
  starts_at: string;
  ends_at: string | null;
  max_uses: number | null;
  uses: number;
  note: string;
  active: boolean;
  created_by: string | null;
  created_at: string;
}

/** `GET /admin/promo-codes/:id/redemptions` row. */
export interface PromoRedemption {
  id: string;
  account_id: string;
  redeemed_at: string;
  expires_at: string;
}

export interface TransferVersion {
  id: string;
  /** Null = the global price every wallet without a row of its own settles at. */
  fund_wallet_id: string | null;
  sun_per_energy_day_bp: number;
  effective_from: string;
  changed_by: string | null;
  reason: string | null;
  created_at: string;
}

export interface UtilisationBand {
  above_bps: number;
  surcharge_bps: number;
}

export interface LadderVersion {
  id: string;
  brand_id: string | null;
  utilisation_bands: UtilisationBand[] | null;
  margin_instant_bp: number;
  margin_market_bp: number;
  margin_deep_bp: number;
  hold_back_bp: number;
  min_level_amount: number;
  max_levels: number;
  slippage_bp: number;
  effective_from: string;
  changed_by: string | null;
  reason: string | null;
  created_at: string;
}

export interface LadderNeutralDefaults {
  utilisation_bands: UtilisationBand[];
  margin_instant_bp: number;
  margin_market_bp: number;
  margin_deep_bp: number;
  hold_back_bp: number;
  slippage_bp: number;
}

/** One window of a day-part schedule (pricing.md §1.2b). Minutes from 00:00 UTC; end < start wraps. */
export interface DayPartWindow {
  id: string;
  label: string;
  start_utc_minute: number;
  end_utc_minute: number;
  factor_bps: number;
}

/** One published schedule: the whole day in one row, append-only like every price table. */
export interface DayPartScheduleVersion {
  id: string;
  brand_id: string | null;
  windows: DayPartWindow[];
  effective_from: string;
  changed_by: string | null;
  reason: string | null;
  created_at: string;
}

export type ContractFormat = "reserve" | "on_demand" | "on_demand_guaranteed";

export interface B2bContract {
  id: string;
  account_id: string;
  format: ContractFormat;
  price_bp: number;
  /** Always false: a contract price is negotiated and may sit below the live floor (§2). */
  floor_applies: boolean;
  reserve_amount: number | null;
  reserve_addresses: string[];
  term_from: string;
  term_to: string | null;
  overflow_daily_cap_sun: number | null;
  offer_ladder_beyond_cap: boolean;
  supply_policy: string;
  status: "active" | "suspended" | "closed";
  changed_by: string | null;
  reason: string | null;
  closed_at: string | null;
  closed_by: string | null;
  close_reason: string | null;
  created_at: string;
}

export interface AdminOrder {
  id: string;
  client_order_id: string | null;
  account_id: string;
  resource: string;
  amount: number | null;
  delivered_amount: number | null;
  partial: boolean;
  tier: string | null;
  receiver: string;
  source: string;
  status: string;
  confirm_status: string | null;
  price_sun_per_unit: number | null;
  pay_amount_sun: number;
  total_amount_sun: number;
  refunded_amount_sun: number;
  delegate_hashes: string[];
  delegated_at: string | null;
  reclaim_hash: string | null;
  reclaimed_at: string | null;
  expires_at: string | null;
  memo: string | null;
  created_at: string;
  updated_at: string;
  failure: { code: number; slug: string; message: string; at?: string } | null;
  /** What the order is (owner 2026-10-24); absent from an API older than the field. */
  kind?: AdminOrderKind;
  paid_at?: string | null;
  /** A direct transfer's payment block time. */
  payment_block_time?: string | null;
  /** Payment -> delegation on chain; null when nothing was delegated (failed, refunded, in flight). */
  delivery?: { seconds: number; blocks: number; from: "block" | "paid" | "created" } | null;
}

export type AdminOrderKind = "energy" | "bandwidth" | "activation" | "refill" | "renewal" | "promo" | "activator_rental";

/** One way into an account (owner 2026-10-17): e-mails and Telegram names arrive masked for a viewer. */
export interface SignInMethod {
  kind: "wallet" | "google" | "email" | "telegram";
  value: string | null;
  role: string;
  verified: boolean;
  last_used_at: string | null;
}

export interface AdminAccount {
  id: string;
  brand_id: string;
  network: string;
  owner_address: string | null;
  /** The account owners' ways in. */
  sign_in?: SignInMethod[];
  status: string;
  balance_sun: number;
  reserved_sun: number;
  created_at: string;
}

export interface FundWallet {
  address: string;
  label: string | null;
  status: string;
  role: string;
  supplier_id: string | null;
  permission_id: number | null;
  mandate_status: string | null;
  operations_hex: string | null;
  staked_energy_sun: number;
  can_delegate_max_sun: number;
  delegated_out_by_us_sun: number;
  energy_limit: number;
  energy_used: number;
  drift_strikes: number;
  last_chain_sync_at: string | null;
}

export interface PoolLink {
  id: string;
  pool_wallet: string;
  fleet_wallet: string;
  lent_sun: string;
  operator: "platform" | "external";
  min_interval_s: number;
  trigger_locked_bps: number;
  daily_bandwidth_budget: number;
  bandwidth_day: string | null;
  bandwidth_spent: number;
  state: string;
  status: string;
  flush_count: number;
  last_flush_at: string | null;
  last_error: string | null;
  created_by: string | null;
  reason: string | null;
  created_at: string;
}

export interface AlertAcknowledgement {
  /** The editor's address. */
  by: string | null;
  at: string;
  reason: string;
}

export interface AdminAlert {
  kind: string;
  severity: string;
  target: string;
  detail: string;
  at: string;
  /** `kind:target`, what an acknowledgement is filed under. */
  key?: string;
  /** The newest acknowledgement inside 24 h, or null. */
  acknowledged?: AlertAcknowledgement | null;
}

// ---- operator reads added 2026-09-25 (apps/api/src/routes/admin-ops.ts) ----------------------

export interface QueueCounts {
  queue: string;
  waiting: number;
  active: number;
  delayed: number;
  failed: number;
  completed: number;
  paused: number;
}

export interface QueuesView {
  as_of: string;
  /** False when the API could not count; `data` is then empty and `reason` says why. */
  available: boolean;
  reason: string | null;
  data: QueueCounts[];
}

export interface ProviderAccount {
  provider: string;
  display_name: string | null;
  enabled: boolean;
  /** In the worker's PROVIDER_LIVE_ALLOWLIST; a venue that is not can have its policy set but not be switched on. */
  live?: boolean;
  priority: number;
  health_ok: boolean;
  consecutive_failures: number;
  last_error: string | null;
  last_error_at: string | null;
  last_success_at: string | null;
  balance_sun: number | null;
  balance_read_at: string | null;
  low_water_sun: number | null;
  topup_sun: number | null;
  /** "Top up to": the level a top-up refills to; low_water_sun is the trigger. */
  topup_target_sun?: number | null;
  daily_topup_cap_sun: number | null;
  deposit_address: string | null;
  deposit_address_seen_at: string | null;
  updated_at: string | null;
  /** Allocation cost of our orders at this provider over the last 24 h. */
  spend_24h_sun?: number;
  /** Newest chain_tx with purpose provider_topup for this provider. */
  last_topup?: { txid: string; amount_sun: number | null; status: string; at: string } | null;
  /** The top-up pass's latest verdict in plain words; null until the worker first judges this provider. */
  last_decision?: string | null;
  last_decision_at?: string | null;
}

/** `GET /v1/admin/overview` (apps/api/src/routes/admin-overview.ts): the Monitoring page in one read, cached 30 s. */
export interface OwnStakeWallet {
  address: string;
  label: string | null;
  status: string;
  staked_energy_sun: number;
  staked_bandwidth_sun: number;
  energy_limit: number;
  net_limit: number;
  /** What the wallet may delegate now (the allocator's free number), in SUN of stake. */
  free_sun: number;
  /** The same in energy at the network ratio; null when the chain read failed. */
  free_energy: number | null;
  /** The worker's last chain sync of this wallet. */
  synced_at: string | null;
}

export interface OwnStakeView {
  wallets: OwnStakeWallet[];
  delegated: { energy: { count: number; units: number }; bandwidth: { count: number; units: number } };
  reclaims: { due_1h: number; late: number; oldest_late_at: string | null; late_after_seconds: number };
}

export interface OverviewView {
  as_of: string;
  cache_seconds: number;
  router: {
    orders_1h: number;
    orders_24h: number;
    by_status: Record<string, { hour: number; day: number }>;
    failed_24h: number;
    failed_by_reason: Array<{ slug: string; count: number }>;
    delivery: { basis: string; median_seconds: number | null; max_seconds: number | null; delivered_24h: number };
    last_order: {
      at: string;
      delegated_at: string | null;
      status: string;
      resource: string;
      amount: number | null;
      tier: string | null;
    } | null;
    scanner_last_run_at: string | null;
  };
  providers: {
    topups_today: { count: number; sun: number };
    enabled: number;
    total: number;
    healthy: number;
    below_mark: number;
    spend_24h_sun: number;
    data: ProviderAccount[];
  };
  /** Own-pool delivery: null while no fund wallet holds a stake. */
  own_stake: OwnStakeView | null;
  business: {
    orders_today: number;
    orders_7d: number;
    revenue_today_sun: number;
    revenue_7d_sun: number;
    new_customers_today: number;
    new_customers_7d: number;
    signups_7d: number;
    signups_ordered_7d: number;
    signups_prev_7d: number;
    signups_ordered_prev_7d: number;
    top_customers_7d: Array<{ owner: string | null; spent_sun: number; orders: number }>;
    top_share_7d: number | null;
    wallets: {
      as_of: string;
      customers_sun: number;
      customers_usdt: number;
      collector_sun: number | null;
      collector_free_sun: number | null;
      complete: boolean;
    } | null;
  };
}

export type WalletRole = "activator" | "payment" | "collector" | "fund";

/** `GET /v1/admin/wallets` row: one of our addresses with live chain data (cached 60 s). */
export interface AdminWallet {
  role: WalletRole;
  label: string;
  address: string;
  /** Counted into "on our wallets" (fund wallets are not). */
  counted: boolean;
  status: "ok" | "low" | "empty" | "unknown";
  trx_sun: number | null;
  usdt: number | null;
  energy_available: number | null;
  energy_limit: number | null;
  bandwidth_available: number | null;
  bandwidth_limit: number | null;
  last_tx_at: string | null;
  last_txid: string | null;
  /** Reads that failed this time, by name; a missed last transaction keeps its last good value when there is one. */
  read_failed: Array<"account" | "usdt" | "last_transaction">;
  read_at: string;
}

export interface AdminWalletsView {
  as_of: string;
  cache_seconds: number;
  summary: {
    customers_sun: number;
    customers_usdt: number;
    wallets_sun: number;
    wallets_usdt: number;
    free_sun: number;
    collector_sun: number | null;
    collector_free_sun: number | null;
    complete: boolean;
  };
  data: AdminWallet[];
}

/** `GET /v1/admin/topup-settings`: the global "Minimum top-up" in SUN; null = the worker default. */
export interface TopupSettings {
  min_transfer_sun: number | null;
  default_min_transfer_sun: number;
  /** "Low-water by default": the trigger of a venue whose own low-water is empty; null = none. */
  default_low_water_sun?: number | null;
}

export interface ProviderPolicyPatch {
  enabled?: boolean;
  low_water_sun?: number;
  daily_topup_cap_sun?: number;
  /** null clears it. */
  topup_target_sun?: number | null;
  reason?: string;
}

export interface JustLendRental {
  allocation_id: string;
  order_id: string;
  receiver: string;
  state: string;
  amount_requested: number;
  amount_delivered: number | null;
  prepaid_sun: number | null;
  final_cost_sun: number | null;
  refund_sun: number | null;
  delegate_txid: string | null;
  return_txid: string | null;
  expires_at: string | null;
  overdue: boolean;
  unsettled: boolean;
  last_error: string | null;
  created_at: string | null;
}

export interface JustLendView {
  as_of: string;
  source_id: string;
  summary: {
    open: number;
    overdue: number;
    unsettled_returns: number;
    /** Null unless every open rental carries a prepay figure. */
    open_prepaid_sun: number | null;
  };
  data: JustLendRental[];
  recent_settled: JustLendRental[];
}

export interface OrderFill {
  class: string;
  amount: number;
  /** The price this part was sold at. */
  price_centi_sun: number | null;
  /** The ladder level stored with the order, which the allocator fills from. */
  ladder_price_centi_sun: number | null;
  source_ids: string[];
}

export interface OrderAllocation {
  id: string;
  seq: number;
  source_id: string;
  source_kind: string;
  supply_address: string | null;
  external_order_id: string | null;
  state: string;
  mode: string;
  amount_requested: number;
  amount_delivered: number | null;
  cost_sun: number | null;
  final_cost_sun: number | null;
  refund_sun: number | null;
  sun_delegated: number | null;
  consumed_energy: number | null;
  delegate_txid: string | null;
  undelegate_txid: string | null;
  tx_hashes: string[];
  attempt: number;
  last_error_code: string | null;
  last_error: string | null;
  expires_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface OrderChainTx {
  txid: string;
  kind: string;
  purpose: string;
  status: string;
  owner_address: string;
  receiver: string | null;
  sun: number | null;
  allocation_id: string | null;
  seq: number | null;
  broadcast_at: string | null;
  block_number: number | null;
  fee_sun: number | null;
  net_usage: number | null;
  result: string | null;
  rebroadcast_count: number;
  expiration: string | null;
  created_at: string | null;
}

export interface OrderEvent {
  seq: number;
  event: string;
  from_status: string | null;
  to_status: string | null;
  actor: string;
  detail: unknown;
  created_at: string | null;
}

export interface AdminOrderDetail {
  order: AdminOrder;
  fills: OrderFill[];
  /** `venue_offers`: a transfer priced from the venues' offers when it landed, not from the ladder walk. */
  fills_priced_by: "ladder" | "venue_offers";
  unit_price_centi_sun: number | null;
  /** The sum of the order's allocations (proven charge, else quote; failed attempts left out). */
  cost_sun: number | null;
  allocations: OrderAllocation[];
  chain_txs: OrderChainTx[];
  events: OrderEvent[];
}

export interface AdminLedgerLine {
  journal_id: string;
  kind: string;
  reference_type: string | null;
  reference_id: string | null;
  memo: string | null;
  counterparty_address: string | null;
  block_number: number | null;
  amount_sun: number;
  amount_usdt: number;
  created_at: string;
}

export interface AdminLedgerView {
  account_id: string;
  balances: Array<{ currency: string; balance: number }>;
  data: AdminLedgerLine[];
  next_cursor: string | null;
}

export interface DepthSource {
  source_id: string;
  resource: string;
  tier: string;
  class: string;
  available_amount: number;
  cost_centi_sun_per_unit: number;
  utilisation_bp: number | null;
  observed_at: string;
  age_seconds: number;
  /** Inside the ladder builder's freshness window; a stale row is skipped. */
  fresh: boolean;
}

export interface AuditRow {
  id: string;
  actor_type: string;
  actor_address: string | null;
  actor_id: string | null;
  action: string;
  target_type: string | null;
  target_id: string | null;
  reason: string;
  before: unknown;
  after: unknown;
  request_ip: string | null;
  brand_id: string | null;
  created_at: string;
}

// ---- the fund's two reports (b2b-pricing.md §3) ----------------------------------------------

export interface DemandChannelRow {
  day: string;
  day_part: string;
  channel: string;
  format: string;
  orders: number;
  energy_sold: number;
  energy_ordered: number;
  revenue_sun: number;
  refusals: number;
  p_centi_sun: number;
  u_micro: number;
  s_micro: number;
  q_centi_sun: number;
  /** Null when `s = 1`: every unit came from a provider, so `k` is unbounded. */
  k_micro: number | null;
  /** Null for the same reason: there is no own capacity for `E` to be the price OF. */
  e_centi_sun: number | null;
  own_energy: number;
  provider_energy: number;
  provider_cost_sun: number;
  overflow_pnl_sun: number;
  stake_needed_sun: number | null;
}

export interface DemandLadderRow {
  day: string;
  day_part: string;
  level_class: string;
  energy: number;
  revenue_sun: number;
  realised_price_centi_sun: number;
  floor_centi_sun: number | null;
  price_over_floor_centi_sun: number | null;
  cost_sun: number;
  margin_sun: number;
}

export interface OwnLevelsExhausted {
  count: number;
  energy: number;
  first_at: string | null;
  last_at: string | null;
  day_parts: Array<{ day_part: string; count: number }>;
}

export interface DemandReport {
  from: string;
  to: string;
  generated_at: string;
  ratio_micro: number;
  scales: { price: string; ratios: string; money: string };
  totals: {
    orders: number;
    energy_sold: number;
    revenue_sun: number;
    provider_energy: number;
    provider_cost_sun: number;
    refusals: number;
    overflow_pnl_sun: number;
    stake_needed_sun: number;
    overflow_share_micro: number;
  };
  data: DemandChannelRow[];
  ladder: { by_class: DemandLadderRow[]; own_levels_exhausted: OwnLevelsExhausted };
}

export interface SettlementRow {
  wallet_address: string;
  day: string;
  sun_days: number;
  energy_consumed: number;
  amount_sun: number;
  entries: number;
  samples: number;
}

export interface SettlementStatement {
  from: string;
  to: string;
  total_amount_sun: number;
  data: SettlementRow[];
}

// ---- the public ask ladder, read by the admin like anybody else ------------------------------

export interface OrderBookLevel {
  price_sun: number;
  amount: number;
  class: "instant" | "market" | "deep";
}

export interface OrderBookView {
  resource: string;
  tier: string;
  as_of: string;
  valid_until: string;
  floor_sun: number | null;
  /** Total sellable amount across every level. */
  depth: number;
  levels: OrderBookLevel[];
  walk: unknown;
  cheaper_from: string | null;
}

/** `GET /v1/admin/accounts/{id}/deposit-addresses` (docs/deposit-addresses.md). */
export interface AdminDepositAddress {
  address: string;
  account_index: number;
  rotation: number;
  derivation_path: string;
  status: "active" | "retired";
  created_at: string;
  retired_at: string | null;
  retired_by: string | null;
  reason: string | null;
  credited_until: string | null;
}

export interface AdminDepositAllowance {
  used_in_window: number;
  remaining_in_window: number;
  last_rotated_at: string | null;
  next_allowed_at: string | null;
  limits: { min_interval_days: number; max_per_window: number; window_days: number };
}

export interface AdminDepositView {
  account_id: string;
  configured: boolean;
  active: AdminDepositAddress | null;
  history: AdminDepositAddress[];
  allowance: AdminDepositAllowance;
}

/** One row of `GET /v1/admin/subscriptions` (apps/api/src/routes/admin-products.ts). */
export interface AdminSubscription {
  id: string;
  account_id: string;
  receiver: string;
  resource: string;
  /** The plan slug; null on a legacy watcher. */
  plan: string | null;
  plan_name: string | null;
  /** The stored status, `grace` included (the customer view folds it into `suspended`). */
  status: "active" | "paused" | "suspended" | "cancelled" | "grace";
  reserve_energy: number | null;
  delegated_in: number;
  next_billing_at: string | null;
  created_at: string;
  updated_at: string;
}

/** One row of `GET /v1/admin/node-keys`. The key is never stored; the row id comes masked. */
export interface AdminNodeKey {
  key_masked: string;
  label: string | null;
  account_id: string;
  status: "active" | "revoked";
  created_at: string;
  /** Always null: there is no revocation timestamp column. */
  revoked_at: null;
  last_used_at: string | null;
}

/** `GET /v1/admin/accounts/{id}/profile`: one customer on one screen. Secrets never appear here. */
export interface AdminAccountProfile {
  account: {
    id: string;
    brand_id: string;
    network: string;
    owner_address: string | null;
    sign_in?: SignInMethod[];
    label: string | null;
    status: string;
    created_at: string;
    first_deposit_at: string | null;
    first_deposit_sun: number | null;
    first_order_at: string | null;
    last_order_at: string | null;
    came_by: {
      first_credit: "deposit address" | "wallet payment" | null;
      has_api_keys: boolean;
      compat_dialects: string[];
    };
    /** How they first found the site (referrer host, UTM, landing path); null before 2026-10-11 or when not sent. */
    first_touch: FirstTouch | null;
  };
  money: {
    balance_sun: number;
    reserved_sun: number;
    deposited_sun: number;
    deposits: number;
    spent_sun: number;
    charged_orders: number;
    refunded_sun: number;
    refunds: number;
    average_order_sun: number | null;
  };
  deposits: Array<{
    journal_id: string;
    created_at: string;
    amount_sun: number;
    usdt_amount: string | null;
    from: string | null;
    how: "deposit address" | "wallet payment";
    block_number: number | null;
    txid: string | null;
  }>;
  orders: {
    counts: { all: number; completed: number; active: number; failed: number };
    limit: number;
    data: AdminProfileOrder[];
  };
  receivers: Array<{ receiver: string; orders: number; spent_sun: number; last_order_at: string; own_wallet: boolean }>;
  integration: {
    api_keys: Array<{
      label: string;
      created_at: string;
      last_used_at: string | null;
      scopes: string[];
      compat_dialect: string | null;
      active: boolean;
    }>;
    webhooks: Array<{
      host: string | null;
      role: string;
      events: string[] | null;
      format: string;
      active: boolean;
      last_delivery_at: string | null;
      last_delivery_status: number | null;
    }>;
    compat_orders_7d: Array<{ dialect: string; orders: number }>;
    /** False: no table records compatible-API calls, only the orders they created. */
    compat_calls_recorded: boolean;
    node_keys: Array<{
      label: string | null;
      active: boolean;
      created_at: string;
      last_used_at: string | null;
      daily_limit_sun: number | null;
      limit_action: string;
    }>;
  };
  note: { text: string | null; updated_at: string | null; updated_by: string | null };
}

/** First-touch attribution as the API stores it (`accounts.first_touch`); every key optional. */
export interface FirstTouch {
  ref_host?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  landing_path?: string;
  at?: string;
}

export interface AdminProfileOrder {
  id: string;
  created_at: string;
  status: string;
  status_label: string;
  status_group: "completed" | "active" | "failed";
  failure: string | null;
  resource: string;
  amount: number | null;
  tier: string | null;
  receiver: string;
  price_sun_per_unit: number | null;
  paid_sun: number;
  filled_by: string | null;
  source: string;
  txid: string | null;
}

/** `GET /v1/admin/mcp-usage` (apps/api routes/admin-mcp-usage.ts): MCP usage counts per UTC day. */
export interface McpUsageTop {
  name: string;
  calls: number;
  errors: number;
  distinct_callers: number;
}

export interface McpUsageView {
  since: string;
  days: number;
  data: Array<{
    day: string;
    connects: number;
    calls: number;
    errors: number;
    distinct_callers: number;
    calls_by_auth: { anonymous: number; key: number; oauth: number };
  }>;
  top_clients: McpUsageTop[];
  top_tools: McpUsageTop[];
}

/** GET /v1/admin/network: the analyzer's collector and the by-day table (viewer). Amounts raw: SUN; USDT 6-decimal units. */
export interface NetworkView {
  as_of: string;
  /** False when the analyzer is not configured or none of its reads answered. */
  connected: boolean;
  /** Per block of the page, why its read failed. */
  errors: Record<string, string>;
  collector: {
    last_block: number | null;
    lag_seconds: number | null;
    history_pct: number | null;
    history_reached_at: string | null;
    calls_today: number | null;
    calls_cap: number;
    disk_used_pct: number | null;
    disk_total_bytes: number | null;
  } | null;
  days: Array<{
    day: string;
    blocks: number | null;
    partial: boolean;
    transactions: number | null;
    transfers_with_energy: number | null;
    burned_tx: number | null;
    burned_trx_sun: number | null;
    usdt_transfers: number | null;
    /** 6-decimal units as a decimal string: a day's total passes 2^53. */
    usdt_moved: string | null;
    /** Delegate events, energy and bandwidth together. */
    delegations: number | null;
    new_accounts: number | null;
  }>;
}

export type NetworkTab = "overview" | "demand" | "providers" | "provider" | "burners" | "usdt" | "activations" | "address" | "watch";
export type NetworkWindowKind = "24h" | "7d" | "30d" | "today" | "all" | "custom";
/** One watch target (routes/admin-network-tabs.ts WatchRow). Amounts: TRX in SUN, USDT in 6-decimal units, this week. */
export interface WatchTargetRow {
  role: "client" | "payer" | "parent" | "activator";
  address: string;
  venues: string[];
  week_trx_sun: number;
  week_usdt_units: number;
  payments: number;
  first_seen: string;
  decile: number;
  /** Parent / activator: the targets it stands behind. */
  of: string[];
  activator: string;
  activator_source: string;
  /** private | shared | venue | none | not looked up yet */
  activator_kind?: string;
  token_sent_at?: string | null;
}
/** The per-block split v2 summed over rows (apps/chain-analyzer/src/events.ts SPLIT_KEYS); `*_trx` are whole TRX. */
export type SplitMap = Partial<Record<string, number>>;
export interface UsdtPartyRow {
  address: string;
  transfers: number;
  /** 6-decimal units, a decimal string. */
  usdt_moved: string;
  /** Distinct receivers (senders' table) or senders (receivers' table). */
  peer_count: number;
  /** False when the window is over a day: the peer count is the sketch (~0.5 %). */
  distinct_exact?: boolean;
}

/**
 * GET /v1/admin/network/tabs/:tab (viewer): one tab's queries for the window, rows as the analyzer answered them
 * (routes/admin-network-tabs.ts). A failed query is in `errors` under its key; the others still come.
 */
export interface NetworkTabView {
  as_of: string;
  tab: NetworkTab;
  window: { kind: NetworkWindowKind; from: string; to: string };
  address: string | null;
  owners: string[] | null;
  connected: boolean;
  errors: Partial<Record<string, string>>;
  /** Blocks served from their last good read (the read failed now): when that read was. */
  stale?: Partial<Record<string, string>>;
  data: {
    /** `current` 1 = the window, 0 = the window of the same length before it. */
    t1?: Array<{ current: number; blocks: number; blocks_split: number; transactions: number; burned_trx_sun: number; split: SplitMap; delegations: number; new_accounts: number }>;
    t2?: Array<{ usdt_transfers: number | null; usdt_moved: string | null; senders: number }>;
    /** `current` 0 rows are the 24 h before the window, `hour_index` 0 = 24 h before its first hour. */
    /** `bucket_hours` 24 on windows over 7 days: one row per UTC day, `hour_index` = hours from the window start. */
    d1?: Array<{ hour_index: number; current: number; bucket_hours?: number; blocks: number; blocks_split: number; transactions: number; split: SplitMap }>;
    /** `weekday` 1 = Monday; sums over `days` days. */
    d2?: Array<{ weekday: number; hour: number; days: number; blocks_split: number; transactions: number; split: SplitMap }>;
    d3?: Array<{ address: string; delegations: number; delegated_sun: number; receiver_count: number; sun_by_hour_index: Record<string, number>; bucket_hours?: number }>;
    p1?: Array<{ provider: string; addresses: number; delegations: number; delegated_sun: number; share_pct: number | null; receivers: number; owner_addresses: string[] }>;
    /** One provider row (tab `provider`, `owners` = its owner_addresses): receivers active in the window. */
    p2?: Array<{ new_receivers: number; returning_receivers: number }>;
    b1?: Array<{ address: string; burned_trx_sun: number; calls: number; partial_calls_pct: number; energy_burned_pct: number; first_burn_hour: string; last_burn_hour: string; had_energy_7d: number }>;
    b2?: Array<{ address: string; day: string; burned_trx_sun: number }>;
    u1?: UsdtPartyRow[];
    u2?: UsdtPartyRow[];
    a1?: Array<{ address: string; activated: number; by_account_create: number; by_transfer: number; first_at: string; last_at: string }>;
    /** Watch targets of the last 7 days (docs/product/chain-watch.md); `decile` 10 = the biggest spenders of the role, 0 for parents. */
    w1?: WatchTargetRow[];
    /** Watched venue wallets other than deposits: pay-in, collector, anchor; `origin` admin | seed | auto | replay. */
    w2?: Array<{ address: string; kind: string; venue: string; origin: string; note: string; added_at: string }>;
    /** Client deposit addresses the watch knows, per venue and origin. */
    w3?: Array<{ venue: string; origin: string; deposits: number; last_added: string }>;
    c1?: Array<{ activator: string; activated_at: string; rule: string }>;
    c1Label?: Array<{ service: string; role: string; source: string; note: string; updated: string }>;
    c2?: Array<{ day: string; calls: number; stake_energy: number; burned_energy: number; burned_trx_sun: number }>;
    c3?: Array<{ delegator: string; net_sun: number; first_at: string; last_at: string }>;
    /** `amount` raw units of the token (SUN for TRX), a decimal string. */
    c4?: Array<{ day: string; token_or_op: string; direction: string; transfers: number; amount: string }>;
  };
}

/** Own supply console (own-supply-console-spec.md): one pool wallet's figures from its latest chain snapshot. */
export interface OwnPool {
  address: string;
  label: string | null;
  status: string;
  supplier_id: string;
  snapshot_at: string | null;
  snapshot_block: number | null;
  stake: { energy_sun: number | null; energy_delegated_sun: number | null; bandwidth_sun: number | null; balance_sun: number | null };
  energy: { limit_per_day: number | null; used: number | null; free_now: number | null; fill_now: number | null; energy_per_trx: number | null };
  window: { now_s: number | null; median_24h_s: number | null; median_7d_s: number | null; target_s: number; chain_window_s: number | null };
  turnover: { limits_sold_24h: number | null; limits_theoretical: number | null };
  money: {
    revenue_24h_sun: number;
    revenue_7d_sun: number;
    energy_sold_24h: number;
    energy_sold_7d: number;
    realised_price_sun: number | null;
    cost_price_sun: number | null;
    supplier_rate_centi_sun: number | null;
    yield_gross: number | null;
    yield_net: number | null;
  };
  bandwidth: {
    staked_sun: number | null;
    daily_budget: number | null;
    left_now: number | null;
    used_24h: number;
    used_7d_per_day: number;
    fee_burned_7d_sun: number;
    txs_24h: number;
  };
  fill_24h: Array<{ at: string; fill: number | null; window_s: number; used: number }>;
}

export interface OwnSupplyAlert {
  kind: string;
  level: "bad" | "attention";
  pool: string | null;
  message: string;
  since: string | null;
}

export interface OwnReclaims {
  open: number;
  due_1h: number;
  overdue: number;
  oldest_overdue_at: string | null;
  overdue_after_s: number;
}

export interface OwnSupplyView {
  as_of: string;
  pools: OwnPool[];
  reclaims: OwnReclaims;
  alerts: OwnSupplyAlert[];
}

export interface OwnPoolSettings {
  target_window_minutes: number;
  supplier_rate_centi_sun: number | null;
  updated_by: string | null;
  updated_at: string | null;
}

export interface OwnPoolDetail {
  as_of: string;
  pool: OwnPool;
  alerts: OwnSupplyAlert[];
  permissions: ({ read_at: string | null } & Record<string, unknown>) | null;
  settings: OwnPoolSettings;
}

export interface OwnDelegation {
  allocation_id: string;
  order_id: string;
  pool: string | null;
  receiver: string;
  resource: string;
  tier: string | null;
  amount: number;
  delegated_at: string | null;
  reclaim_due_at: string | null;
  reclaimed_at: string | null;
  status: "active" | "due_1h" | "overdue" | "reclaimed";
  consumed_energy: number | null;
}

export interface OwnDelegationsView {
  as_of: string;
  totals: { active: number; active_energy: number; mean_amount: number | null; mean_held_s: number | null; refill_share_7d: number | null };
  reclaims: OwnReclaims;
  data: OwnDelegation[];
}

export interface OwnAdmissionView {
  as_of: string;
  days: number;
  inputs_recorded: boolean;
  summary: { orders: number; taken_share: number | null; missed: number; missed_energy: number };
  data: Array<{
    order_id: string;
    created_at: string;
    amount: number;
    taken: "own" | "split" | "venue";
    own_units: number;
    venue_units: number;
    venues: string | null;
    pool: string | null;
    free_energy_then: number | null;
    could_take_whole: boolean;
    unit_price_centi_sun: number | null;
    venue_cost_sun: number;
    /** What the router saw at its first ranking; null for orders before it was recorded. */
    inputs: { own_available: number; best_venue: { source_id: string; unit_sun: number } | null; first: string | null } | null;
  }>;
}

export interface OwnEconomicsView {
  as_of: string;
  days: number;
  revenue_sun: number;
  energy_sold: number;
  delegations: number;
  realised_price_sun: number | null;
  cost_price_sun: number | null;
  margin_per_unit_sun: number | null;
  bandwidth_per_order: number | null;
  fee_burned_sun: number;
  yield_gross: number | null;
  yield_net: number | null;
  venue_unit_cost_sun: number | null;
  venue_cost_of_same_energy_sun: number | null;
}

/** The Finance page (GET /v1/admin/finance), owner approved 2026-10-21. All amounts in SUN unless named otherwise. */
export interface FinanceDay {
  day: string;
  paid_sun: number;
  refunds_sun: number;
  venue_cost_sun: number;
  own_cost_sun: number;
  chain_cost_sun: number;
  margin_sun: number;
  net_sun: number;
}

export interface FinanceCause {
  sun: number;
  orders: number;
  top: Array<{ order_id: string; at: string; sun: number }>;
}

export interface FinanceEconomicsRow {
  orders: number;
  revenue_sun: number;
  avg_revenue_sun: number;
  avg_margin_sun: number;
  avg_chain_cost_sun: number;
}

export interface FinanceView {
  as_of: string;
  period: "day" | "week" | "month" | "all";
  from: string | null;
  profit: {
    paid_sun: number;
    refunds_sun: number;
    venue_cost_sun: number;
    own_stake_cost_sun: number;
    margin_sun: number;
    chain_cost_sun: number;
    chain_cost_by_kind: Record<string, number>;
    net_sun: number;
    daily: FinanceDay[];
    /** Paid and still being placed: left out of the figures above until they settle. */
    in_progress: { orders: number; paid_sun: number };
  };
  balance: {
    wallets_sun: number | null;
    wallets_usdt: number | null;
    wallets: Array<{ role: string; label: string; address: string; trx_sun: number | null; usdt: number | null }>;
    venue_credit_sun: number;
    venues: Array<{ provider: string; name: string | null; balance_sun: number; live: boolean; read_at: string | null }>;
    customers_sun: number | null;
    customers_usdt: number | null;
    equity_sun: number | null;
    complete: boolean;
  };
  leakage: {
    expected_fee_sun: number;
    real_margin_sun: number;
    gap_sun: number;
    venue_overcharge: FinanceCause;
    negative_margin: FinanceCause;
    refunds: FinanceCause;
  };
  economics: {
    orders: number;
    by_method: Array<FinanceEconomicsRow & { method: string }>;
    top_clients: Array<FinanceEconomicsRow & { account_id: string; owner: string | null }>;
  };
  flow: {
    deposits_trx_sun: number;
    deposits_usdt: number;
    transfers_in_sun: number;
    transfers_in: number;
    withdrawals_sun: number;
    withdrawals: number;
    sweeps_sun: number;
    sweeps: number;
    venue_topups_sun: number;
    venue_topups: number;
  };
}

/** GET /v1/admin/providers/delivery: delivered vs ordered per venue and resource (owner 2026-10-21). */
export interface DeliveryDeltaRow {
  provider: string;
  resource: string;
  deliveries: number;
  exact: number;
  short: number;
  over: number;
  /** Units delivered minus units ordered, the middle delivery. */
  median_delta: number;
  min_delta: number;
  max_delta: number;
  /** The worst shortfall as parts per million of the amount ordered (negative = short). */
  min_delta_ppm: number;
}

export interface DeliveryDeltaView {
  as_of: string;
  days: 7 | 30;
  data: DeliveryDeltaRow[];
}

/** GET /v1/admin/forecast/venues: the price map's model on every series with a minute history (owner 2026-10-21). */
export interface ForecastSeries {
  source: string;
  mode: "order" | "refill";
  resource: "energy" | "bandwidth";
  tier: string | null;
  param: "price" | "min" | "max" | "available" | "fee" | "utilization" | "capacity" | `config:${string}`;
  unit: "sun_per_unit" | "units" | "sun" | "percent" | "value";
  origin: "market_prices" | "venue_quote_history" | "refill_pool_history" | "venue_config_history";
  /** The next 24 hours from the current UTC hour on. */
  hours: Array<{ hour: number; value: number | null; low: number | null; high: number | null }>;
}

export interface VenueForecastView {
  as_of: string;
  lookback_days: number;
  series: ForecastSeries[];
}

/** GET /v1/admin/forecast/terms: each venue's newest answer per resource and rental term (owner 2026-11-01). */
export interface TermPrice {
  source: string;
  resource: "energy" | "bandwidth";
  tier: string;
  /** SUN per unit for the whole term, without the fixed fee. */
  unit_sun: number | null;
  min_amount: number | null;
  fixed_sun: number;
  available: number;
  /** When the answer last changed (the worker writes a row only on a change). */
  changed_at: string;
}

export interface TermPricesView {
  as_of: string;
  tiers: string[];
  prices: TermPrice[];
}

/** One node check summary (GET /v1/admin/pools): the last 24 h of microtron's checks against the node. */
export interface PoolCheck {
  checks: number;
  exact: number;
  /** Largest |model − node| energy debt (network: energy weight) in the 24 h. */
  max_abs_diff: number;
  last_at: string;
  last_ok: boolean;
  last_stake_ok: boolean;
  last_debt_diff: number;
  last_window_diff: number;
}

/** One tracked pool at the current second; energy in energy units, stake in TRX. */
export interface PoolView {
  address: string;
  block: number;
  at: string;
  own_energy_trx: number;
  delegated_out_energy_trx: number;
  acquired_energy_trx: number;
  v1_energy_trx: number;
  unfreezing_trx: number;
  energy_limit: number;
  energy_used: number;
  energy_free: number;
  /** Free energy for sure while a large reclaim waits for the node's confirmation; = energy_free otherwise. */
  energy_free_min: number;
  can_delegate_trx: number;
  can_delegate_energy: number;
  can_delegate_energy_1h: number;
  energy_free_1h: number;
  window_hours: number;
  recovery_per_hour: number;
  own_bandwidth_trx: number;
  delegated_out_bandwidth_trx: number;
  acquired_bandwidth_trx: number;
  bandwidth_limit: number;
  bandwidth_used: number;
  bandwidth_free: number;
  bandwidth_can_delegate_trx: number;
  corridor_open: boolean;
  check: PoolCheck | null;
}

export interface ProviderPools {
  provider: string;
  pools: PoolView[];
  totals: Pick<PoolView, "own_energy_trx" | "delegated_out_energy_trx" | "acquired_energy_trx" | "energy_limit" | "energy_used" | "energy_free"
    | "energy_free_min" | "energy_free_1h" | "can_delegate_trx" | "can_delegate_energy" | "can_delegate_energy_1h" | "bandwidth_free" | "bandwidth_can_delegate_trx">;
}

export interface PoolsNetwork {
  block: number;
  lag_seconds: number;
  energy_weight_trx: number;
  energy_per_trx: number;
  usdt_transfer_stake_trx: number;
  check: PoolCheck | null;
}

/** GET /v1/admin/pools (viewer): every tracked pool, by provider, most energy to give out first. */
export interface PoolsView {
  as_of: string;
  connected: boolean;
  errors: Record<string, string>;
  network: PoolsNetwork | null;
  providers: ProviderPools[];
}

/** GET /v1/admin/pools/:provider (viewer). */
export interface ProviderPoolsDetail extends ProviderPools {
  as_of: string;
  network: PoolsNetwork | null;
  /** Free energy of the provider's pools in N minutes, if nothing is spent or reclaimed meanwhile. */
  forecast: Array<{ minutes: number; free: number }>;
  /** Free energy at the end of each of the last 24 hours. */
  history: Array<{ hour: string; free: number }>;
  history_error: string | null;
}
